import { useParams } from "react-router";
import { useApi } from "@/hooks/useApi";
import { useAuth } from "@/hooks/useAuth";
import { useEncryption } from "@/hooks/useEncryption";
import { useHoliday } from "@/hooks/useHoliday";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { LoadingSpinner } from "@/components/shared/LoadingSpinner";
import { EmptyState } from "@/components/shared/EmptyState";
import { showToast } from "@/components/shared/Toast";
import { MarkdownText } from "@/components/shared/MarkdownText";
import { ChoreTimer } from "@/components/chores/ChoreTimer";
import { Check, Lock, Timer, Zap, Palmtree } from "lucide-react";
import { useState, useEffect, useMemo } from "react";
import { type PendingInfo } from "@/hooks/useChoreTimer";
import { formatTimeRemaining } from "@/lib/chore-scheduler";

interface Chore {
  _key: string;
  name: string;
  icon: string;
  description: string;
  frequency_in_days: number;
  weekdays: string;
  atHour: number;
  gracetime_in_hours: number;
  reward_points: number;
  punishment_points: number;
  max_points: number | null;
  pending: PendingInfo | null;
}

function GraceCountdown({ expiresAt }: { expiresAt: string }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const ms = new Date(expiresAt).getTime() - now;
  const label = ms > 0 ? formatTimeRemaining(ms) : "0s";
  return (
    <span className="inline-flex items-center gap-1 text-info px-1.5 py-0.5 rounded bg-app-success/10 text-app-success">
      <Timer className="w-3 h-3" />
      ⏳ {label} left
    </span>
  );
}

function ChoreCardWithTimer({
  chore,
  completing,
  onComplete,
  currentPoints,
}: {
  chore: Chore;
  completing: string | null;
  onComplete: (key: string) => void;
  currentPoints: number;
}) {
  const pointsPaused = chore.max_points != null && currentPoints >= chore.max_points;
  const status = chore.pending?.status ?? "pending";

  const canComplete = !pointsPaused && status === "due";
  const isLocked = pointsPaused || status === "pending";
  const isDone = !pointsPaused && status === "completed";
  const isOverdue = !pointsPaused && status === "failed";

  const borderColor = pointsPaused
    ? "border-l-app-warning"
    : isDone
    ? "border-l-app-text-muted"
    : isOverdue
      ? "border-l-app-danger"
      : canComplete
        ? "border-l-app-success"
        : "border-l-app-info";

  const iconBg = pointsPaused
    ? "bg-app-warning/10"
    : isDone
    ? "bg-app-text-muted/10"
    : isOverdue
      ? "bg-app-danger/10"
      : canComplete
        ? "bg-app-success/10"
        : "bg-app-info/10";

  return (
    <Card
      className={`border-app-border border-l-4 ${borderColor} hover:shadow-md hover:bg-app-surface-raised/50 transition-all duration-200 overflow-hidden py-0 gap-0 ${isDone || isOverdue || pointsPaused ? "opacity-60" : ""}`}
    >
      <CardContent className="p-2 md:p-2.5">
        <div className="flex items-center gap-2.5">
          <div className={`w-8 h-8 rounded-lg ${iconBg} flex items-center justify-center text-lg shrink-0`}>
            {chore.icon}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-body font-semibold">{chore.name}</h3>
              {pointsPaused ? (
                <span className="text-xs bg-app-warning/10 text-app-warning px-2 py-0.5 rounded-full font-bold inline-flex items-center gap-1">
                  <Zap className="w-3 h-3" /> Paused above {chore.max_points} pts
                </span>
              ) : canComplete && chore.pending ? (
                <GraceCountdown expiresAt={chore.pending.expires_at} />
              ) : (
                <ChoreTimer pending={chore.pending} compact />
              )}
            </div>
            {chore.description && (
              <MarkdownText text={chore.description} className="text-info text-app-text-muted mt-0.5" />
            )}
            <div className="flex flex-wrap gap-1.5 mt-1">
              <span className="text-xs bg-app-surface-raised px-2 py-0.5 rounded-full font-medium text-app-text-muted">
                Every {chore.frequency_in_days}d
              </span>
              {chore.weekdays && (
                <span className="text-xs bg-app-surface-raised px-2 py-0.5 rounded-full font-medium text-app-text-muted">
                  {chore.weekdays}
                </span>
              )}
              <span className="text-xs bg-app-surface-raised px-2 py-0.5 rounded-full font-medium text-app-text-muted">
                🕐 {chore.atHour}:00
              </span>
            </div>
            <div className="flex flex-wrap gap-1.5 mt-1">
              <span className="text-xs bg-app-success/10 text-app-success px-2 py-0.5 rounded-full font-semibold">
                +{chore.reward_points} pts
              </span>
              <span className="text-xs bg-app-danger/10 text-app-danger px-2 py-0.5 rounded-full font-semibold">
                -{chore.punishment_points} pts
              </span>
            </div>
          </div>
          {canComplete ? (
            <Button
              size="icon"
              className="bg-app-success hover:bg-app-success/80 shrink-0 rounded-lg w-8 h-8 shadow-sm transition-all duration-200 hover:shadow-md hover:scale-105 active:scale-95"
              onClick={() => onComplete(chore._key)}
              disabled={completing === chore._key}
            >
              <Check className="w-5 h-5" />
            </Button>
          ) : pointsPaused ? (
            <div className="shrink-0 rounded-lg w-8 h-8 bg-app-warning/10 flex items-center justify-center" title={`Paused — points at or above ${chore.max_points}`}>
              <Zap className="w-4 h-4 text-app-warning" />
            </div>
          ) : isLocked ? (
            <div className="shrink-0 rounded-lg w-8 h-8 bg-app-info/10 flex items-center justify-center" title="Not active yet">
              <Lock className="w-4 h-4 text-app-info" />
            </div>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
}

export function ClientChores() {
  const { jwt } = useParams();
  const { getHeaders, user } = useAuth();
  const { decryptList, hasKey } = useEncryption(user?._key);
  const holiday = useHoliday();
  const { data: rawData, loading, error, refetch } = useApi<{ chores: Chore[]; current_points: number }>(
    `/api/client/${jwt}/chores`,
  );
  const [completing, setCompleting] = useState<string | null>(null);
  const [chores, setChores] = useState<Chore[] | null>(null);
  const currentPoints = rawData?.current_points ?? 0;

  // Decrypt chore names/descriptions
  useEffect(() => {
    if (!rawData?.chores) return;
    decryptList(rawData.chores as unknown as Record<string, unknown>[], ["name", "description"])
      .then((d) => setChores(d as unknown as Chore[]));
  }, [rawData?.chores, decryptList]);

  // Sort chores by urgency: due first (by expires_at), then pending (by due_at), then completed/failed last
  // NOTE: useMemo must be before early returns to preserve hooks order across re-renders
  const sortedChores = useMemo(() => {
    if (!chores) return [];
    return [...chores].sort((a, b) => {
      // Paused (due to points filter) at the bottom
      const aPaused = a.max_points != null && currentPoints >= a.max_points;
      const bPaused = b.max_points != null && currentPoints >= b.max_points;
      if (aPaused !== bPaused) return aPaused ? 1 : -1;
      // Paused: sort by max_points (limit) descending
      if (aPaused && bPaused) return (b.max_points ?? 0) - (a.max_points ?? 0);

      const statusA = a.pending?.status ?? "pending";
      const statusB = b.pending?.status ?? "pending";

      const priority: Record<string, number> = { due: 0, pending: 1, failed: 2, completed: 3 };
      const pA = priority[statusA] ?? 4;
      const pB = priority[statusB] ?? 4;
      if (pA !== pB) return pA - pB;

      // Within same status, sort by relevant timestamp
      if (statusA === "due" && a.pending && b.pending) {
        return new Date(a.pending.expires_at).getTime() - new Date(b.pending.expires_at).getTime();
      }
      if (statusA === "pending" && a.pending && b.pending) {
        return new Date(a.pending.due_at).getTime() - new Date(b.pending.due_at).getTime();
      }
      return 0;
    });
  }, [chores, currentPoints]);

  if (loading) return <LoadingSpinner />;
  if (error) return <p className="text-app-danger text-body p-4">{error}</p>;

  const handleComplete = async (choreKey: string) => {
    setCompleting(choreKey);
    try {
      const res = await fetch(`/api/client/${jwt}/chores/${choreKey}/complete`, {
        method: "POST",
        headers: getHeaders(),
      });
      if (!res.ok) throw new Error((await res.json()).error);
      showToast.success("Chore completed! 🎉");
      refetch();
    } catch (err) {
      showToast.error(err instanceof Error ? err.message : "Failed to complete chore");
    } finally {
      setCompleting(null);
    }
  };

  if (!chores || chores.length === 0) {
    return <EmptyState icon="✨" title="No chores assigned" description="Your admin hasn't set up any chores yet." />;
  }

  return (
    <div className="space-y-3 md:space-y-4">
      <h1 className="text-heading font-bold">Your Chores</h1>
      {holiday.active && (
        <Card className="border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30 py-0">
          <CardContent className="flex items-center gap-3 p-3">
            <Palmtree className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0" />
            <p className="text-sm text-amber-700 dark:text-amber-300">
              🏖️ Chores are paused during holiday mode.
            </p>
          </CardContent>
        </Card>
      )}
      <div className="space-y-2">
        {sortedChores.map((chore) => (
          <ChoreCardWithTimer
            key={chore._key}
            chore={chore}
            completing={completing}
            onComplete={handleComplete}
            currentPoints={currentPoints}
          />
        ))}
      </div>
    </div>
  );
}
